import { Pane, StatusMessage, Loading } from "components/ui"
import { Button, StackView } from "@planningcenter/tapestry-react"
import { arrayOf, bool, element, func, oneOfType, string, shape } from "prop-types"
import { colors } from "components/shared/admin_colors"

const statuses = {
  pending: { type: "pending" },
  saving: { type: "saving", text: "Saving...", icon: "spinner" },
  success: { type: "success", text: "Saved!", icon: "general#check" },
  error: {
    type: "error",
    text: "Changes not saved.",
    icon: "general#exclamation-triangle"
  }
}

const SettingPane = ({
  title,
  isEditing,
  onIsEditing,
  onSave,
  onCancel,
  error = null,
  status = null,
  isLoading = false,
  useBlankState = false,
  isChangePending = false,
  blankState = null,
  editBody = null,
  staticBody = null,
  children = null,
  headerContent = null,
  editButtonContent = null,
  isValid = true,
  showEditButton = true,
  footer
}) => {
  let paneStatus = !status && isChangePending ? "pending" : status

  return (
    <Pane>
      <Pane.Header axis="vertical" alignment="flex-start" distribution="center">
        <StackView
          axis="horizontal"
          alignment="center"
          distribution="space-between"
          spacing={1}
          width="100%"
        >
          <Pane.Title>{title}</Pane.Title>
          <StackView axis="horizontal" alignment="center" spacing={1}>
            {paneStatus && <StatusMessage {...statuses[paneStatus]} />}
            {showEditButton && !isLoading && !useBlankState && !isEditing && (
              <Button onClick={() => onIsEditing(true)} variant="outline" size="xs" title="Edit" />
            )}

            {!isLoading && !useBlankState && isEditing && (
              <StackView axis="horizontal" spacing={1}>
                {editButtonContent}
                <Button
                  disabled={paneStatus === "saving"}
                  onClick={onCancel}
                  size="xs"
                  variant="outline"
                  title="Cancel"
                />
                <Button
                  disabled={!isValid || paneStatus !== "pending" || paneStatus === "saving"}
                  onClick={onSave}
                  size="xs"
                  theme="create"
                  title="Save"
                />
              </StackView>
            )}
          </StackView>
        </StackView>
        {headerContent}
      </Pane.Header>

      {isLoading && (
        <Pane.Body>
          <Loading />
        </Pane.Body>
      )}

      {!isLoading && !isEditing && useBlankState && <Pane.Body>{blankState}</Pane.Body>}

      {!isLoading && !useBlankState && (
        <Pane.Body>
          <div style={{ color: colors.error }}>
            {paneStatus !== "saving" && error && error.detail}
          </div>
          {isEditing ? editBody : staticBody}
          {children}
        </Pane.Body>
      )}

      {footer && !isLoading && !useBlankState && <Pane.Footer>{footer}</Pane.Footer>}
    </Pane>
  )
}

SettingPane.propTypes = {
  title: oneOfType([arrayOf(element), element, string]).isRequired,
  isEditing: bool.isRequired,
  onIsEditing: func.isRequired,
  onSave: func.isRequired,
  onCancel: func.isRequired,
  error: shape({ detail: string }),
  status: string,
  isLoading: bool,
  useBlankState: bool,
  isChangePending: bool,
  blankState: element,
  editBody: element,
  staticBody: element,
  children: oneOfType([element, arrayOf(element)]),
  headerContent: element,
  editButtonContent: element,
  isValid: bool,
  showEditButton: bool,
  footer: element
}

export default SettingPane
